import { asLensDocument, lensCardCount } from '~/pages/LeLens/document/types';
import { DEFAULT_PRINT_SETTINGS } from '../print/printSettings';

import type { LensSchedule, LensScheduleFilterSet, LensScheduleInput } from '~/hooks/apis/reporting/useLensSchedules';

import type { Lens, LensFilter, LensFilterValues, LensPeriod, LensSpec } from '~/types/leLens';

/** The same limits svc-reporting enforces (src/modules/le-lens/schedules/schedule.ts). */
export const SCHEDULE_LIMITS = { filterSets: 40, recipients: 50, label: 80, name: 120 } as const;
export const ALLOWED_RECIPIENT = /^[a-z0-9._%+-]+@luxuryescapes\.com$/i;

export type ScheduleDraft = LensScheduleInput & { recurrence: 'daily' | 'weekly' | 'monthly'; sendTime: string };

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const isValidSendTime = (time: string) => /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time);

/** Missing timing fields belong to the original monthly-at-8am schedules. */
export function scheduleTiming(schedule: Pick<LensSchedule, 'recurrence' | 'sendTime'>) {
  return { recurrence: schedule.recurrence ?? ('monthly' as const), sendTime: schedule.sendTime ?? '08:00' };
}

export function scheduleDescription(
  schedule: Pick<LensSchedule, 'recurrence' | 'sendTime' | 'dayOfMonth' | 'weekdays' | 'timezone' | 'deliveryFormat'>,
): string {
  const { recurrence, sendTime } = scheduleTiming(schedule);
  const frequency =
    recurrence === 'daily'
      ? 'Every day'
      : recurrence === 'weekly'
        ? (schedule.weekdays ?? []).map((day) => WEEKDAYS[day - 1]).join(', ')
        : `${ordinal(schedule.dayOfMonth)} of each month`;
  return `${frequency} at ${sendTime} ${schedule.timezone && schedule.timezone !== 'Australia/Sydney' ? schedule.timezone : 'Sydney time'}`;
}

export function emptyDraft(lens: Pick<Lens, 'title'>, ownerEmail: string | undefined): ScheduleDraft {
  return {
    name: lens.title.slice(0, SCHEDULE_LIMITS.name),
    recurrence: 'daily',
    sendTime: '08:00',
    weekdays: [1, 2, 3, 4, 5],
    timezone: 'Australia/Sydney',
    deliveryFormat: 'image_and_pdf',
    dayOfMonth: 1,
    period: null,
    filterSets: [],
    recipients: ownerEmail ? [ownerEmail.toLowerCase()] : [],
    enabled: true,
    includeInternal: false,
  };
}

const isBlank = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && !Object.values(value).some(Boolean));

/** The Lens's own filters as they are set on screen: saved defaults, then the reader's choices. Never the period or a cross-filter. */
export function screenFilters(lens: Pick<Lens, 'spec'>, values: LensFilterValues): LensFilterValues {
  const declared = scheduleFilters(lens);
  return Object.fromEntries(
    declared
      .map((filter) => [filter.id, values[filter.id] ?? filter.defaultValue] as const)
      .filter(([, value]) => !isBlank(value)),
  );
}

function describeValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (value && typeof value === 'object') {
    const range = value as { from?: string; to?: string };
    return [range.from, range.to].filter(Boolean).join(' to ');
  }
  return String(value ?? '');
}

function filterParts(lens: Pick<Lens, 'spec'>, filters: LensFilterValues): Array<string> {
  return scheduleFilters(lens)
    .map((filter) => describeValue(filters[filter.id]))
    .filter(Boolean);
}

/** A readable name for a set of filter values, e.g. "Accor · Transaction date"; "Whole Lens" with none. */
export function labelFor(lens: Pick<Lens, 'spec'>, filters: LensFilterValues): string {
  return (filterParts(lens, filters).join(' · ') || 'Whole Lens').slice(0, SCHEDULE_LIMITS.label);
}

/** What a PDF will show, in words, next to its name. */
export function describeFilters(lens: Pick<Lens, 'spec'>, filters: LensFilterValues): string {
  if (!Object.keys(filters).length && scheduleFilters(lens).length) return 'Report defaults, resolved when it sends';
  const parts = scheduleFilters(lens)
    .map((filter) => {
      const value = describeValue(filters[filter.id]);
      return value ? `${filter.label}: ${value}` : '';
    })
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No filters: the whole Lens';
}

export function scheduleFilters(lens: Pick<Lens, 'spec'>): Array<LensFilter> {
  const spec = asLensDocument(lens.spec);
  if (!spec) {
    return (lens.spec as LensSpec).filters ?? [];
  }
  return spec.filters.map((filter) => ({
    id: filter.column,
    column: filter.column,
    label: filter.label ?? filter.column.replace(/_/g, ' '),
    type: 'select',
    multiple: true,
    ...(filter.value ? { defaultValue: filter.value } : {}),
  }));
}

export const lensHasCards = (lens: Pick<Lens, 'spec'>) => lensCardCount(lens.spec) > 0;
export const lensHasFilters = (lens: Pick<Lens, 'spec'>) => scheduleFilters(lens).length > 0;

/**
 * Filter values as the PDF is drawn with them: saved defaults first, empty values dropped, keys in
 * order. Two PDFs with the same key are the same PDF. Matches svc-reporting's effectiveFilterKey.
 */
export function effectiveFilterKey(lens: Pick<Lens, 'spec'>, filters: LensFilterValues): string {
  const defaults = Object.fromEntries(
    scheduleFilters(lens)
      .filter((filter) => filter.defaultValue !== undefined)
      .map((filter) => [filter.id, filter.defaultValue]),
  );
  const merged: Record<string, unknown> = { ...defaults, ...filters };
  const sortDeep = (value: unknown): unknown =>
    Array.isArray(value)
      ? [...value].map(sortDeep).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
      : value && typeof value === 'object'
        ? Object.fromEntries(
            Object.keys(value)
              .sort()
              .map((key) => [key, sortDeep((value as Record<string, unknown>)[key])]),
          )
        : value;
  const kept = Object.keys(merged)
    .sort()
    .filter((key) => {
      const value = merged[key];
      return !(value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0));
    })
    .map((key) => [key, sortDeep(merged[key])]);
  return JSON.stringify(kept);
}

function reportSetKey(lens: Pick<Lens, 'spec'>, set: LensScheduleFilterSet): string {
  return JSON.stringify([
    effectiveFilterKey(lens, set.filters),
    set.scope ?? null,
    set.scope === 'selected' ? [...(set.tabs ?? [])].sort() : [],
    set.print ?? DEFAULT_PRINT_SETTINGS,
  ]);
}

/** One PDF per value of one filter, every other filter as it is on screen (Neil: one per chain). */
export function setsForEachValue(
  lens: Pick<Lens, 'spec'>,
  base: LensFilterValues,
  filterId: string,
  values: Array<string>,
): Array<LensScheduleFilterSet> {
  const filter = scheduleFilters(lens).find((candidate) => candidate.id === filterId);
  return values.map((value) => ({
    label: value.slice(0, SCHEDULE_LIMITS.label),
    filters: { ...base, [filterId]: filter?.multiple ? [value] : value },
  }));
}

/** Adds sets, skipping any whose name or filters are already there; says how many were left out. */
export function mergeSets(
  lens: Pick<Lens, 'spec'>,
  current: Array<LensScheduleFilterSet>,
  added: Array<LensScheduleFilterSet>,
) {
  const labels = new Set(current.map((set) => set.label.trim().toLowerCase()));
  const keys = new Set(current.map((set) => reportSetKey(lens, set)));
  const kept = added.filter((set) => {
    const label = set.label.trim().toLowerCase();
    const key = reportSetKey(lens, set);
    if (labels.has(label) || keys.has(key)) {
      return false;
    }
    labels.add(label);
    keys.add(key);
    return true;
  });
  const room = Math.max(0, SCHEDULE_LIMITS.filterSets - current.length);
  return { sets: [...current, ...kept.slice(0, room)], skipped: added.length - Math.min(kept.length, room) };
}

/** What is wrong with a draft, in words, or null. The server checks the same and more. */
export function draftProblem(draft: ScheduleDraft, lens?: Pick<Lens, 'spec'>): string | null {
  if (lens && !lensHasCards(lens)) {
    return 'This Lens has no cards yet, so there is nothing to put in a PDF. Add a card to the Lens first.';
  }
  if (!draft.name.trim()) {
    return 'Give the schedule a name.';
  }
  if (draft.recurrence !== 'daily' && draft.recurrence !== 'weekly' && draft.recurrence !== 'monthly') {
    return 'Choose daily, weekly or monthly.';
  }
  if (draft.recurrence === 'weekly' && !draft.weekdays?.length) return 'Choose at least one weekday.';
  try {
    new Intl.DateTimeFormat('en', { timeZone: draft.timezone ?? 'Australia/Sydney' }).format();
  } catch {
    return 'Choose a valid timezone.';
  }
  if (!isValidSendTime(draft.sendTime)) {
    return 'Choose a Sydney time from 00:00 to 23:59.';
  }
  if (
    draft.recurrence === 'monthly' &&
    (!Number.isInteger(draft.dayOfMonth) || draft.dayOfMonth < 1 || draft.dayOfMonth > 28)
  ) {
    return 'Choose a day from 1 to 28.';
  }
  if (!draft.filterSets.length) {
    return 'Add at least one PDF.';
  }
  if (draft.filterSets.length > SCHEDULE_LIMITS.filterSets) {
    return `One email can carry up to ${SCHEDULE_LIMITS.filterSets} PDFs.`;
  }
  const labels = draft.filterSets.map((set) => set.label.trim().toLowerCase());
  if (labels.some((label) => !label)) {
    return 'Every PDF needs a name.';
  }
  if (new Set(labels).size !== labels.length) {
    return 'Two PDFs have the same name.';
  }
  if (lens) {
    const seen = new Map<string, string>();
    for (const set of draft.filterSets) {
      const key = reportSetKey(lens, set);
      const earlier = seen.get(key);
      if (earlier !== undefined) {
        return `"${earlier}" and "${set.label}" have the same filters, so they would be the same PDF. Remove one, or give it different filters.`;
      }
      seen.set(key, set.label);
    }
  }
  if (!draft.recipients.length) {
    return 'Add at least one recipient.';
  }
  if (draft.recipients.length > SCHEDULE_LIMITS.recipients) {
    return `Up to ${SCHEDULE_LIMITS.recipients} recipients.`;
  }
  const outside = draft.recipients.find((email) => !ALLOWED_RECIPIENT.test(email));
  if (outside) {
    return `${outside} is not a Luxury Escapes address. Scheduled reports go to luxuryescapes.com addresses only.`;
  }
  return null;
}

/** "1st", "2nd", "23rd". */
export function ordinal(day: number): string {
  const tens = day % 100;
  const suffix =
    tens >= 11 && tens <= 13 ? 'th' : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th');
  return `${day}${suffix}`;
}

/** Whether a period moves with each send (rolling) or names fixed dates. */
export function isFixedPeriod(period: LensPeriod | null): boolean {
  return Boolean(period && typeof period.window === 'object' && 'from' in period.window);
}
